import { Link, useRouterState } from '@tanstack/react-router';
import { Menu } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Mark } from '../brand/Mark';
import { IconHint } from '../hint';
import { Button } from '../../ui/button';
import { Separator } from '../../ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../ui/sheet';
import { sh } from './messages';

/**
 * The rail's drawer form below 1024px, opened from the topbar. Built directly
 * on `Sheet` rather than the registry `Sidebar`'s own mobile variant, which
 * only switches below 768px and would leave 768-1023 showing the rail inline
 * with no room for it.
 */
export function ShellDrawer(props: {
  brand: { label: string; href: string };
  switcher?: ReactNode;
  nav: ReactNode;
  footer?: ReactNode;
}): ReactNode {
  // Open for as long as the path it was opened at is the one on screen, so any
  // navigation closes it -- a switcher row in its popover included -- without
  // an effect listening for the route. A link click closes it on its own: a
  // link to the current path, or one differing only in its search, leaves the
  // pathname unchanged.
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [openedAt, setOpenedAt] = useState<string | null>(null);

  return (
    <Sheet
      open={openedAt === pathname}
      onOpenChange={(open) => setOpenedAt(open ? pathname : null)}
    >
      <IconHint label={sh('toggle')}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="size-7">
            <Menu aria-hidden />
          </Button>
        </SheetTrigger>
      </IconHint>
      <SheetContent
        side="left"
        className="flex w-80 flex-col gap-0 p-0"
        onClick={(event) => {
          if ((event.target as Element).closest('a[href]')) setOpenedAt(null);
        }}
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{props.brand.label}</SheetTitle>
          <SheetDescription>{sh('toggle')}</SheetDescription>
        </SheetHeader>
        <div className="flex items-center gap-2 border-b p-4">
          <Link to={props.brand.href} className="flex items-center gap-2">
            <Mark aria-hidden />
            <span className="text-title-sm">{props.brand.label}</span>
          </Link>
        </div>
        {props.switcher !== undefined && <div className="p-2">{props.switcher}</div>}
        <div className="flex-1 overflow-auto p-2">{props.nav}</div>
        {props.footer !== undefined && (
          <>
            <Separator />
            <div className="flex flex-col gap-2 p-2">{props.footer}</div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
